import React from 'react';
import { Box, Chip, Divider, Grid, Stack, Typography, useTheme } from '@mui/material';

import Bloco from './componentes/Bloco';
import BlocoBloqueado from './componentes/BlocoBloqueado';
import TituloDaTela from './componentes/TituloDaTela';
import ListaDeIndicadores from './componentes/ListaDeIndicadores';
import CoberturaChip from '../components/CoberturaChip';
import { ABAS } from './abas';
import { SECOES } from '../diagnostico/catalogo';
import { estadoDaSecao, montarSecao, formatarPorTipo, formatarMoedaCurta } from './apresentacao';
import { rotuloDaCompetencia } from '../services/loteDoMes';
import { LARGURA_DE_LEITURA } from './layout';

/**
 * Balanço Patrimonial — o que a empresa tem, o que ela deve, e o que sobra.
 *
 * POR QUE ESTA TELA FOI REESCRITA: a versão anterior calculava por conta
 * própria e chegava a outro número. Para a MESMA empresa, no mesmo instante,
 * ela exibia Capital de Giro R$ 0 enquanto Caixa e Ciclo exibia R$ 45.000, e
 * carimbava "Crítico" numa liquidez seca de 0,84 que a outra tela classificava
 * como "Atenção". A causa era um `valor || 0` em toda formatação — ausência
 * virava zero — somado a indicadores lidos de `balanco.indicadores`, que o
 * questionário guiado preenche pela metade.
 *
 * Agora ela lê da MESMA leitura das outras telas e não classifica nada por
 * conta própria. Liquidez mora em Caixa e Ciclo, retorno mora em Dívida e
 * Retorno, e aqui fica o que só existe aqui: a demonstração, a composição do
 * que a empresa tem e deve, e a estrutura de capital.
 *
 * Duas regras herdadas do redesign valem linha a linha:
 * 1. Ausência é travessão, nunca R$ 0,00 — em financeiro, zero é uma medição.
 * 2. O que o total do grupo tem a mais do que as linhas detalhadas aparece como
 *    "Não detalhado", em vez de sumir sem explicação ou de forçar a soma.
 */

/** Linhas do ativo, na ordem em que um contador lê. */
const LINHAS_DO_ATIVO = [
  ['circulante', 'ATIVO CIRCULANTE', [
    ['caixa', 'Caixa'],
    ['bancos', 'Bancos'],
    ['aplicacoes', 'Aplicações financeiras'],
    ['contasAReceber', 'Contas a receber'],
    ['estoque', 'Estoque']
  ]],
  ['naoCirculante', 'ATIVO NÃO CIRCULANTE', [
    ['contasAReceberLP', 'Contas a receber de longo prazo'],
    ['emprestimosASocios', 'Empréstimos a sócios'],
    ['investimentos', 'Investimentos'],
    ['imobilizado', 'Imobilizado']
  ]]
];

/** Linhas do passivo e do patrimônio líquido. */
const LINHAS_DO_PASSIVO = [
  ['circulante', 'PASSIVO CIRCULANTE', [
    ['fornecedores', 'Fornecedores'],
    // Na planilha do cliente esta linha é a PROVISÃO de custos fixos a pagar —
    // uma obrigação, não a conta de resultado de mesmo nome. O rótulo diz isso,
    // porque "Custos Fixos" dentro do passivo lê como erro de classificação.
    ['custosFixos', 'Custos fixos a pagar'],
    ['encargosFiscais', 'Encargos fiscais'],
    ['impostos', 'Impostos a pagar'],
    ['parcelamentosImpostos', 'Parcelamentos de impostos'],
    ['financiamentos', 'Financiamentos de curto prazo'],
    ['contasAtrasadas', 'Contas atrasadas']
  ]],
  ['naoCirculante', 'PASSIVO NÃO CIRCULANTE', [
    ['financiamentosLP', 'Financiamentos de longo prazo'],
    ['emprestimosDeSocios', 'Empréstimos de sócios'],
    ['parcelamentosImpostosLP', 'Parcelamentos de impostos (longo prazo)']
  ]],
  ['patrimonioLiquido', 'PATRIMÔNIO LÍQUIDO', [
    ['capitalSocial', 'Capital social'],
    ['lucroAcumulado', 'Lucros acumulados']
  ]]
];

const numeroOuNulo = (valor) => {
  if (valor === null || valor === undefined || valor === '') return null;
  const n = Number(valor);
  return Number.isFinite(n) ? n : null;
};

/**
 * Monta um grupo da demonstração: só as contas com valor, mais a diferença até
 * o total do grupo quando ela existe.
 */
const montarGrupo = (bloco, rotulo, contas) => {
  const total = numeroOuNulo(bloco?.total);
  const linhas = contas
    .map(([chave, nome]) => ({ nome, valor: numeroOuNulo(bloco?.[chave]) }))
    .filter((linha) => linha.valor !== null);

  const somaDetalhada = linhas.reduce((soma, linha) => soma + linha.valor, 0);
  const naoDetalhado = total === null ? null : total - somaDetalhada;

  // Um centavo de diferença é arredondamento da planilha, não conta faltando.
  // E "não detalhado" só informa alguma coisa ao lado de contas detalhadas: num
  // grupo em que nada foi aberto — o questionário só coleta o total do PL —, a
  // linha repetiria o total do grupo logo acima dela.
  if (linhas.length && naoDetalhado !== null && Math.abs(naoDetalhado) >= 1) {
    linhas.push({ nome: 'Não detalhado na planilha', valor: naoDetalhado, derivada: true });
  }

  return { rotulo, total, linhas };
};

const Demonstracao = ({ titulo, grupos, totalRotulo, total }) => (
  <Box>
    {/* h3, e não h2: esta caixa vive dentro de um `Bloco`, cujo título "A
        demonstração" já é o h2 que a contém. "Ativo" e "Passivo + Patrimônio
        Líquido" no mesmo nível do bloco que os abriga desmonta o sumário que um
        leitor de tela monta a partir da hierarquia. */}
    <Typography variant="h5" component="h3" sx={{ mb: 1.5 }}>
      {titulo}
    </Typography>

    {grupos.map((grupo) => (
      <Box key={grupo.rotulo} sx={{ mb: 2 }}>
        <Stack direction="row" justifyContent="space-between" sx={{ py: 0.75 }}>
          <Typography variant="subtitle2">{grupo.rotulo}</Typography>
          <Typography variant="subtitle2">{formatarPorTipo(grupo.total, 'moeda')}</Typography>
        </Stack>
        <Divider />
        {grupo.linhas.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
            Sem contas detalhadas neste grupo.
          </Typography>
        ) : (
          grupo.linhas.map((linha) => (
            <Stack
              key={linha.nome}
              direction="row"
              justifyContent="space-between"
              sx={{ py: 0.6, pl: 2 }}
            >
              <Typography
                variant="body2"
                color={linha.derivada ? 'text.secondary' : 'text.primary'}
                sx={{ fontStyle: linha.derivada ? 'italic' : 'normal' }}
              >
                {linha.nome}
              </Typography>
              <Typography variant="body2">{formatarPorTipo(linha.valor, 'moeda')}</Typography>
            </Stack>
          ))
        )}
      </Box>
    ))}

    <Divider sx={{ mt: 1 }} />
    <Stack direction="row" justifyContent="space-between" sx={{ pt: 1.25 }}>
      <Typography variant="subtitle1" fontWeight={700}>{totalRotulo}</Typography>
      <Typography variant="subtitle1" fontWeight={700}>{formatarPorTipo(total, 'moeda')}</Typography>
    </Stack>
  </Box>
);

/** Barra de composição com legenda — pizza sem rótulo não se lê. */
const Composicao = ({ titulo, partes, total }) => {
  const tema = useTheme();
  const escuro = tema.palette.mode === 'dark';
  const conhecidas = partes.filter((p) => p.valor !== null && p.valor > 0);
  if (!conhecidas.length || !total) return null;

  // Paleta categórica validada para daltonismo nos dois temas (ΔE deutan >= 9
  // entre pares vizinhos). Os tokens do tema não servem aqui: primary e info
  // saem quase idênticos em barra empilhada, e a leitura vira uma mancha só.
  const CORES = escuro
    ? ['#3987e5', '#d95926', '#199e70', '#c98500', '#d55181']
    : ['#2a78d6', '#eb6834', '#1baf7a', '#eda100', '#e87ba4'];

  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>{titulo}</Typography>
      <Box
        sx={{ display: 'flex', height: 24, borderRadius: 1, overflow: 'hidden', gap: '2px' }}
        role="img"
        aria-label={conhecidas
          .map((p) => `${p.rotulo}: ${formatarPorTipo((p.valor / total) * 100, 'percentual')}`)
          .join('; ')}
      >
        {conhecidas.map((parte, indice) => (
          <Box
            key={parte.rotulo}
            sx={{
              width: `${(parte.valor / total) * 100}%`,
              backgroundColor: CORES[indice % CORES.length]
            }}
          />
        ))}
      </Box>
      <Stack direction="row" spacing={2} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
        {conhecidas.map((parte, indice) => (
          <Stack key={parte.rotulo} direction="row" spacing={0.75} alignItems="center">
            <Box sx={{ width: 10, height: 10, borderRadius: '2px', backgroundColor: CORES[indice % CORES.length] }} />
            <Typography variant="caption" color="text.secondary">
              {parte.rotulo} <strong>{formatarPorTipo((parte.valor / total) * 100, 'percentual')}</strong>
              {' · '}{formatarMoedaCurta(parte.valor)}
            </Typography>
          </Stack>
        ))}
      </Stack>
    </Box>
  );
};

const TelaBalanco = ({ leitura, balanco, onIrPara }) => {
  const irParaDados = () => onIrPara && onIrPara(ABAS.DADOS);
  const referencia = leitura?.referenciaDoBalanco || null;
  const estrutura = estadoDaSecao(leitura, SECOES.ESTRUTURA_CAPITAL);

  if (!balanco) {
    return (
      <Stack spacing={3} sx={{ maxWidth: LARGURA_DE_LEITURA, mx: 'auto' }}>
        <BlocoBloqueado
          titulo="Balanço Patrimonial"
          pergunta="O que a empresa tem, o que ela deve, e o que sobra para os sócios?"
          chamada={estrutura.chamada || 'O balanço ainda não foi informado.'}
          campos={estrutura.campos}
          bloqueados={estrutura.bloqueados}
          onDestravar={irParaDados}
        />
      </Stack>
    );
  }

  const ativo = balanco.ativo || {};
  const passivo = balanco.passivo || {};

  const gruposDoAtivo = LINHAS_DO_ATIVO.map(([chave, rotulo, contas]) =>
    montarGrupo(ativo[chave], rotulo, contas)
  );
  const gruposDoPassivo = LINHAS_DO_PASSIVO.map(([chave, rotulo, contas]) =>
    montarGrupo(passivo[chave], rotulo, contas)
  );

  const ativoTotal = numeroOuNulo(ativo.total);
  const passivoTotal = numeroOuNulo(passivo.total);
  const patrimonio = numeroOuNulo(passivo.patrimonioLiquido?.total);
  const passivoCirculante = numeroOuNulo(passivo.circulante?.total);
  const passivoNaoCirculante = numeroOuNulo(passivo.naoCirculante?.total);
  // DÍVIDA TOTAL SÓ EXISTE COM AS DUAS PARTES.
  //
  // A guarda anterior só devolvia ausência quando os DOIS grupos faltavam. Com
  // um informado e outro não, o ausente entrava como zero e o rótulo "Dívida
  // total" afirmava uma medição que ninguém fez — a empresa com R$ 60.000 de
  // curto prazo e longo prazo não levantado aparecia devendo exatamente
  // R$ 60.000, e quem lê não tem como desconfiar. É o defeito que o cabeçalho
  // deste arquivo promete não cometer.
  const dividaTotal =
    passivoCirculante === null || passivoNaoCirculante === null
      ? null
      : passivoCirculante + passivoNaoCirculante;

  // O balanço fecha por definição contábil. Quando não fecha, é o dado que está
  // torto — e dizer isso é mais útil do que exibir dois totais diferentes sem
  // comentário e deixar o contador descobrir sozinho.
  const diferenca =
    ativoTotal === null || passivoTotal === null ? null : Math.round(ativoTotal - passivoTotal);

  return (
    <Stack spacing={3} sx={{ maxWidth: LARGURA_DE_LEITURA, mx: 'auto' }}>
      <TituloDaTela
        titulo="Balanço Patrimonial"
        descricao={
          referencia
            ? `Saldos na posição de ${referencia.dataRef || rotuloDaCompetencia(referencia.competencia)}. `
              + 'O balanço mede um instante; o DRE mede um intervalo.'
            : 'Saldos de uma data — o balanço mede um instante, e não o período inteiro.'
        }
        acao={<CoberturaChip cobertura={leitura?.cobertura} />}
      />

      {diferenca !== null && Math.abs(diferenca) >= 1 && (
        <Chip
          color="warning"
          variant="outlined"
          sx={{ alignSelf: 'flex-start', height: 'auto', py: 0.75 }}
          label={`Ativo e Passivo + PL diferem em ${formatarMoedaCurta(Math.abs(diferenca))}. Confira o lançamento: um balanço fecha por definição.`}
        />
      )}

      <Bloco
        titulo="A demonstração"
        pergunta="O que a empresa tem, o que ela deve, e o que sobra para os sócios?"
        nota="Contas sem valor informado não aparecem como zero: elas simplesmente não entram, e o que falta para o total do grupo sai como “Não detalhado”."
      >
        <Grid container spacing={{ xs: 3, md: 5 }}>
          <Grid item xs={12} md={6}>
            <Demonstracao
              titulo="Ativo"
              grupos={gruposDoAtivo}
              totalRotulo="ATIVO TOTAL"
              total={ativoTotal}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Demonstracao
              titulo="Passivo + Patrimônio Líquido"
              grupos={gruposDoPassivo}
              totalRotulo="PASSIVO + PL"
              total={passivoTotal}
            />
          </Grid>
        </Grid>
      </Bloco>

      <Bloco
        titulo="Como está distribuído"
        pergunta="Onde o dinheiro está aplicado, e quem financia a empresa?"
      >
        <Composicao
          titulo="Onde o ativo está aplicado"
          total={ativoTotal}
          partes={[
            { rotulo: 'Circulante', valor: numeroOuNulo(ativo.circulante?.total) },
            { rotulo: 'Não circulante', valor: numeroOuNulo(ativo.naoCirculante?.total) }
          ]}
        />
        <Composicao
          titulo="Quem financia a empresa"
          total={passivoTotal}
          partes={[
            { rotulo: 'Dívida de curto prazo', valor: passivoCirculante },
            { rotulo: 'Dívida de longo prazo', valor: passivoNaoCirculante },
            { rotulo: 'Capital dos sócios', valor: patrimonio }
          ]}
        />
        <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
          <Typography variant="caption" color="text.secondary">
            Dívida total: <strong>{formatarPorTipo(dividaTotal, 'moeda')}</strong>
            {dividaTotal === null && (
              <Typography variant="caption" color="text.secondary" component="span">
                {' '}(exige curto e longo prazo informados)
              </Typography>
            )}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Capital dos sócios: <strong>{formatarPorTipo(patrimonio, 'moeda')}</strong>
          </Typography>
        </Stack>
      </Bloco>

      {estrutura.aberta ? (
        <Bloco
          titulo="Estrutura de capital"
          pergunta="Quanto da empresa é dos sócios e quanto é de terceiros?"
          nota="A régua destes indicadores é a mesma da tela Dívida e Retorno — um número, uma faixa, um lugar só."
        >
          <ListaDeIndicadores itens={montarSecao(leitura, SECOES.ESTRUTURA_CAPITAL)} />
        </Bloco>
      ) : (
        <BlocoBloqueado
          titulo="Estrutura de capital"
          pergunta="Quanto da empresa é dos sócios e quanto é de terceiros?"
          chamada={estrutura.chamada}
          campos={estrutura.campos}
          bloqueados={estrutura.bloqueados}
          onDestravar={irParaDados}
        />
      )}

      <Typography variant="caption" color="text.secondary" sx={{ px: 1, maxWidth: '75ch' }}>
        Liquidez e ciclo ficam na tela Caixa e Ciclo; retorno ao sócio, em Dívida e Retorno. Cada
        medida existe em um lugar só, com uma régua só — foi a duplicação que já fez esta tela
        chamar de “crítica” uma liquidez que a outra classificava como saudável.
      </Typography>
    </Stack>
  );
};

export default TelaBalanco;
